// Global Instructions Rule Applied!
//
// "+ Round" modal: create a standalone funding round for a company without
// leaving the current screen (owner decision 2026-08-26 — a round belongs to
// its company; deals link to it optionally). Two callers: the company form's
// Deals & Rounds tab, and the deal Execution tab's Funding Round card
// ("create and link"). Pattern: AddSecurityModal — company-scoped, house form
// ergonomics, reset-on-open. On success the round is dispatched into the
// rounds slice (addRound) so the round full form opens immediately.
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input, InputNumber, Modal, Select } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { addRound } from '../../../state_manager/state_update_actions/rounds/index.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import { useNumberFormat } from '../../../core/utils/inputNumberFormat.ts'
import { createRound } from '../services/roundsApi.ts'

// House form ergonomics: Enter commits the field (blurs) instead of leaving
// the cursor trapped in the input.
const blurOnEnter = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') (e.target as HTMLElement).blur()
}

const DATE_FORMAT = 'MM/DD/YYYY'
const LOOKUP_CATEGORY_FUNDING_STAGE = 'Funding Stage'
const LOOKUP_CATEGORY_SECURITY_TYPE = 'Security Type'

interface AddRoundModalProps {
  open: boolean
  companyId: string | null
  companyName?: string | null
  defaultCurrencyId?: string | null
  // roundName lets callers label the new option locally without a refetch
  onCreated: (roundId: string, roundName?: string) => void
  onCancel: () => void
}

export default function AddRoundModal({
  open,
  companyId,
  companyName,
  defaultCurrencyId,
  onCreated,
  onCancel
}: AddRoundModalProps): React.ReactElement {
  const dispatch = useDispatch()
  const { detailsByCategory } = useLookups()
  const currencies = useSelector((state: any) => state.globalConfigs?.currencies ?? [])
  const currencyFmt = useNumberFormat('currency')

  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [fundingStageId, setFundingStageId] = useState<string | null>(null)
  const [securityTypeId, setSecurityTypeId] = useState<string | null>(null)
  const [currencyId, setCurrencyId] = useState<string | null>(defaultCurrencyId ?? null)
  const [closeDate, setCloseDate] = useState<string | null>(null)
  const [raiseAmount, setRaiseAmount] = useState<number | null>(null)
  const [preMoney, setPreMoney] = useState<number | null>(null)
  const [postMoney, setPostMoney] = useState<number | null>(null)

  const fundingStageOptions = (detailsByCategory[LOOKUP_CATEGORY_FUNDING_STAGE] || []).map((d) => ({
    value: d.id,
    label: d.label
  }))
  const securityTypeOptions = (detailsByCategory[LOOKUP_CATEGORY_SECURITY_TYPE] || []).map((d) => ({
    value: d.id,
    label: d.label
  }))
  const currencyOptions = useMemo(
    () => currencies.map((c: any) => ({ value: c.id, label: c.code })),
    [currencies]
  )
  const baseCurrencyId = useMemo(
    () => currencies.find((c: any) => c.is_base)?.id ?? null,
    [currencies]
  )
  const ccySymbol = currencies.find((c: any) => c.id === currencyId)?.symbol || '$'

  useEffect(() => {
    if (open) setCurrencyId(defaultCurrencyId ?? baseCurrencyId ?? null)
  }, [open, defaultCurrencyId, baseCurrencyId])

  const reset = (): void => {
    setName('')
    setFundingStageId(null)
    setSecurityTypeId(null)
    setCurrencyId(defaultCurrencyId ?? baseCurrencyId ?? null)
    setCloseDate(null)
    setRaiseAmount(null)
    setPreMoney(null)
    setPostMoney(null)
  }

  const handleOk = async (): Promise<void> => {
    if (!companyId) {
      showErrorToast('The round needs a company')
      return
    }
    if (!name.trim()) {
      showErrorToast('Round name is required')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await createRound(token, {
        account: companyId,
        name: name.trim(),
        funding_stage: fundingStageId,
        security_type: securityTypeId,
        currency: currencyId,
        round_close_date: closeDate,
        raise_amount: raiseAmount,
        pre_money_valuation: preMoney,
        post_money_valuation: postMoney
      })
      if (res.error || !res.roundId) {
        showErrorToast(res.error || 'Failed to create round')
        return
      }
      if (res.data) dispatch(addRound({ round: res.data }))
      toast.success('Round created')
      onCreated(res.roundId, name.trim())
      reset()
    } finally {
      setSaving(false)
    }
  }

  const field = (label: string, control: React.ReactNode) => (
    <div className='flex items-center gap-2 mb-2'>
      <div className='w-32 shrink-0 text-right text-gray-500 text-sm'>{label}</div>
      <div className='flex-1 min-w-0'>{control}</div>
    </div>
  )

  return (
    <Modal
      title={`New round${companyName ? ` — ${companyName}` : ''}`}
      open={open}
      onOk={handleOk}
      okText='Create round'
      confirmLoading={saving}
      onCancel={() => {
        reset()
        onCancel()
      }}
      width={560}
      destroyOnClose
    >
      {field('Name', <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={blurOnEnter} placeholder='e.g. Series A' />)}
      {field(
        'Funding stage',
        <Select
          className='w-full'
          allowClear
          value={fundingStageId ?? undefined}
          options={fundingStageOptions}
          placeholder='Select stage...'
          showSearch
          optionFilterProp='label'
          onChange={(v) => setFundingStageId(v ?? null)}
        />
      )}
      {field(
        'Security type issued',
        <Select
          className='w-full'
          allowClear
          value={securityTypeId ?? undefined}
          options={securityTypeOptions}
          placeholder='Select type...'
          showSearch
          optionFilterProp='label'
          onChange={(v) => setSecurityTypeId(v ?? null)}
        />
      )}
      {field(
        'Currency',
        <Select
          className='w-full'
          value={currencyId}
          options={currencyOptions}
          placeholder='Currency...'
          showSearch
          optionFilterProp='label'
          onChange={(v) => setCurrencyId(v ?? null)}
        />
      )}
      {field(
        'Round close date',
        <DatePicker
          className='w-full'
          format={DATE_FORMAT}
          value={closeDate ? dayjs(closeDate) : null}
          onChange={(d) => setCloseDate(d ? d.format('YYYY-MM-DD') : null)}
        />
      )}
      {field('Raise amount', <InputNumber className='w-full' value={raiseAmount} prefix={ccySymbol} {...currencyFmt} onKeyDown={blurOnEnter} onChange={(v) => setRaiseAmount(v == null ? null : Number(v))} />)}
      {field('Pre-money valuation', <InputNumber className='w-full' value={preMoney} prefix={ccySymbol} {...currencyFmt} onKeyDown={blurOnEnter} onChange={(v) => setPreMoney(v == null ? null : Number(v))} />)}
      {field('Post-money valuation', <InputNumber className='w-full' value={postMoney} prefix={ccySymbol} {...currencyFmt} onKeyDown={blurOnEnter} onChange={(v) => setPostMoney(v == null ? null : Number(v))} />)}
    </Modal>
  )
}
